import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Sparkles, ChevronRight } from 'lucide-react';
import { isMember, isPro } from '@/lib/entitlements';

// PremiumGate — wraps a restricted module/section. When the user isn't entitled,
// the children render blurred + non-interactive behind a frosted-glass overlay with
// an Uber-style upgrade pill. No existing code is removed — just wrapped.
//
// Props:
//  - user: the current user
//  - requirePro: gate a power feature (now Premium — one paid plan)
//  - title / blurb: overlay copy
//  - compact: slim overlay (for HUD bars / small sections)
export default function PremiumGate({ user, requirePro = false, title, blurb, compact = false, preview, children, testId = 'premium-gate' }) {
  const navigate = useNavigate();
  const entitled = requirePro ? isPro(user) : isMember(user);
  if (entitled) return <>{children}</>;

  const heading = title || (requirePro ? 'A Premium feature' : 'A Premium feature');
  const text = blurb || (requirePro
    ? 'Upgrade to Premium to unlock this power-grower tool.'
    : 'Unlock this with Homegrown Premium.');

  // Locked Preview Peek: when a `preview` teaser is supplied, we blur that real
  // sample content behind the glass instead of the (empty) real children.
  const ghost = preview || children;
  const blurClass = preview ? 'blur-[3px] opacity-80' : 'blur-[6px] opacity-60';

  return (
    <div className="relative overflow-hidden rounded-3xl" data-testid={testId}>
      {/* Blurred, inert preview of the content / teaser */}
      <div className={`pointer-events-none select-none ${blurClass}`} aria-hidden="true">
        {ghost}
      </div>
      {/* Frosted glass overlay */}
      <div className={`absolute inset-0 flex flex-col items-center justify-center text-center backdrop-blur-md bg-[#0F2D1F]/34 ${compact ? 'px-4 py-4' : 'px-6 py-10'}`}>
        <div className={`relative flex items-center justify-center rounded-full ${compact ? 'w-9 h-9 mb-2' : 'w-12 h-12 mb-3'}`}
          style={{ background: '#0F2D1F', border: '1px solid rgba(231,182,75,0.6)', boxShadow: '0 0 22px -2px rgba(231,182,75,0.85)' }}>
          <Lock className={`${compact ? 'w-4 h-4' : 'w-5 h-5'}`} style={{ color: '#E7B64B' }} />
        </div>
        <h3 className={`font-bold text-white drop-shadow ${compact ? 'text-sm' : 'text-lg'}`} style={{ fontFamily: 'Outfit, sans-serif' }}>{heading}</h3>
        {!compact && <p className="text-sm text-white/90 mt-1 max-w-xs drop-shadow">{text}</p>}
        <button
          onClick={() => navigate('/paywall')}
          data-testid="premium-upgrade-button"
          className="hg-press mt-4 inline-flex items-center gap-1.5 bg-[#0F2D1F]/55 hover:bg-[#0F2D1F]/70 rounded-full px-4 py-2 text-xs font-bold transition-all"
          style={{ color: '#B4D330' }}
        >
          <Sparkles className="w-3.5 h-3.5" />
          Unlock with Premium
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
